<template>
  <div v-if="showBanner" class="cookie-banner" :class="{ 'show': showBanner }">
    <div class="cookie-banner-content">
      <div class="cookie-info">
        <div class="cookie-icon">
          <i class="bi bi-shield-check"></i>
        </div>
        <div class="cookie-text">
          <h4>Χρήση Cookies</h4>
          <p>
            Χρησιμοποιούμε cookies για να βελτιώσουμε την εμπειρία σας στην ιστοσελίδα μας. 
            Τα cookies μας βοηθούν να παρέχουμε προσωποποιημένο περιεχόμενο και να αναλύουμε την κίνηση της ιστοσελίδας.
          </p>
          <div class="cookie-links">
            <router-link to="/privacy" class="privacy-link">
              Πολιτική Απορρήτου
            </router-link>
            <span class="separator">|</span>
            <router-link to="/terms" class="terms-link">
              Όροι Χρήσης
            </router-link>
          </div>
        </div>
      </div>
      
      <div class="cookie-actions">
        <div class="cookie-buttons">
          <button 
            class="btn btn-outline-secondary btn-sm"
            @click="rejectCookies"
          >
            <i class="bi bi-x-circle me-1"></i>
            Απόρριψη
          </button>
          <button 
            class="btn btn-primary btn-sm"
            @click="acceptCookies"
          >
            <i class="bi bi-check-circle me-1"></i>
            Αποδοχή
          </button>
          <button 
            class="btn btn-outline-primary btn-sm"
            @click="showSettings = true"
          >
            <i class="bi bi-gear me-1"></i>
            Ρυθμίσεις
          </button>
        </div>
      </div>
    </div>

    <!-- Cookie Settings Modal -->
    <div v-if="showSettings" class="cookie-settings-modal">
      <div class="modal-backdrop" @click="showSettings = false"></div>
      <div class="modal-content">
        <div class="modal-header">
          <h3>Ρυθμίσεις Cookies</h3>
          <button class="btn-close" @click="showSettings = false">
            <i class="bi bi-x"></i>
          </button>
        </div>
        
        <div class="modal-body">
          <div class="cookie-category">
            <div class="category-header">
              <label class="category-toggle">
                <input 
                  type="checkbox" 
                  v-model="cookieSettings.necessary" 
                  disabled
                  checked
                />
                <span class="checkmark"></span>
                <span class="category-title">Απαραίτητα Cookies</span>
                <span class="required-badge">Απαραίτητα</span>
              </label>
            </div>
            <p class="category-description">
              Αυτά τα cookies είναι απαραίτητα για τη λειτουργία της ιστοσελίδας και δεν μπορούν να απενεργοποιηθούν.
            </p>
          </div>

          <div class="cookie-category">
            <div class="category-header">
              <label class="category-toggle">
                <input 
                  type="checkbox" 
                  v-model="cookieSettings.functional"
                />
                <span class="checkmark"></span>
                <span class="category-title">Λειτουργικά Cookies</span>
              </label>
            </div>
            <p class="category-description">
              Αυτά τα cookies επιτρέπουν βελτιωμένη λειτουργικότητα και προσωποποίηση (π.χ. αγαπημένα προϊόντα, καλάθι αγορών).
            </p>
          </div>

          <div class="cookie-category">
            <div class="category-header">
              <label class="category-toggle">
                <input 
                  type="checkbox" 
                  v-model="cookieSettings.analytics"
                />
                <span class="checkmark"></span>
                <span class="category-title">Αναλυτικά Cookies</span>
              </label>
            </div>
            <p class="category-description">
              Αυτά τα cookies μας βοηθούν να καταλάβουμε πώς χρησιμοποιείτε την ιστοσελίδα για να τη βελτιώσουμε.
            </p>
          </div>

          <div class="cookie-category">
            <div class="category-header">
              <label class="category-toggle">
                <input 
                  type="checkbox" 
                  v-model="cookieSettings.marketing"
                />
                <span class="checkmark"></span>
                <span class="category-title">Marketing Cookies</span>
              </label>
            </div>
            <p class="category-description">
              Αυτά τα cookies χρησιμοποιούνται για να σας εμφανίσουμε σχετικές διαφημίσεις και προσφορές.
            </p>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-outline-secondary" @click="rejectAll">
            Απόρριψη Όλων
          </button>
          <button class="btn btn-primary" @click="saveSettings">
            Αποθήκευση Ρυθμίσεων
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue'

interface CookieSettings {
  necessary: boolean
  functional: boolean
  analytics: boolean
  marketing: boolean
}

const showBanner = ref(false)
const showSettings = ref(false)

const cookieSettings = reactive<CookieSettings>({
  necessary: true, // Always true and disabled
  functional: false,
  analytics: false,
  marketing: false
})

const COOKIE_CONSENT_KEY = 'cookie-consent'
const COOKIE_SETTINGS_KEY = 'cookie-settings'

const checkCookieConsent = () => {
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY)
  const settings = localStorage.getItem(COOKIE_SETTINGS_KEY)
  
  if (!consent) {
    // Show banner if no consent given
    showBanner.value = true
  } else {
    // Load saved settings
    if (settings) {
      try {
        const savedSettings = JSON.parse(settings)
        Object.assign(cookieSettings, savedSettings)
        applyCookieSettings()
      } catch (error) {
      }
    }
  }
}

const acceptCookies = () => {
  // Accept all cookies
  Object.assign(cookieSettings, {
    necessary: true,
    functional: true,
    analytics: true,
    marketing: true
  })
  
  saveCookieConsent('accepted')
  applyCookieSettings()
  showBanner.value = false
}

const rejectCookies = () => {
  // Only necessary cookies
  Object.assign(cookieSettings, {
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false
  })
  
  saveCookieConsent('rejected')
  applyCookieSettings()
  showBanner.value = false
}

const rejectAll = () => {
  Object.assign(cookieSettings, {
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false
  })
  
  saveCookieConsent('custom')
  applyCookieSettings()
  showSettings.value = false
  showBanner.value = false
}

const saveSettings = () => {
  saveCookieConsent('custom')
  applyCookieSettings()
  showSettings.value = false
  showBanner.value = false
}

const saveCookieConsent = (type: string) => {
  localStorage.setItem(COOKIE_CONSENT_KEY, type)
  localStorage.setItem(COOKIE_SETTINGS_KEY, JSON.stringify(cookieSettings))
}

const applyCookieSettings = () => {
  // Apply analytics cookies (Google Analytics, etc.)
  if (cookieSettings.analytics) {
    // Initialize analytics
    initializeAnalytics()
  } else {
    // Disable analytics
    disableAnalytics()
  }
  
  // Apply marketing cookies
  if (cookieSettings.marketing) {
    initializeMarketing()
  } else {
    disableMarketing()
  }
  
  // Functional cookies are handled by the stores (cart, wishlist, etc.)
  if (!cookieSettings.functional) {
  }
}

const initializeAnalytics = () => {
  // Initialize Google Analytics or other analytics tools
  // Example:
  if (typeof (window as any).gtag !== 'undefined') {
    (window as any).gtag('consent', 'update', {
      'analytics_storage': 'granted'
    })
  }
}

const disableAnalytics = () => {
  // Disable analytics
  if (typeof (window as any).gtag !== 'undefined') {
    (window as any).gtag('consent', 'update', {
      'analytics_storage': 'denied'
    })
  }
}

const initializeMarketing = () => {
  // Initialize marketing tools
  if (typeof (window as any).gtag !== 'undefined') {
    (window as any).gtag('consent', 'update', {
      'ad_storage': 'granted'
    })
  }
}

const disableMarketing = () => {
  // Disable marketing
  if (typeof (window as any).gtag !== 'undefined') {
    (window as any).gtag('consent', 'update', {
      'ad_storage': 'denied'
    })
  }
}

// Expose a method to reset cookie consent (for testing or user request)
const resetCookieConsent = () => {
  localStorage.removeItem(COOKIE_CONSENT_KEY)
  localStorage.removeItem(COOKIE_SETTINGS_KEY)
  showBanner.value = true
}

onMounted(() => {
  // Delay showing the banner slightly to avoid jarring experience
  setTimeout(() => {
    checkCookieConsent()
  }, 1000)
})

// Expose reset function globally for admin or testing
if (typeof window !== 'undefined') {
  (window as any).resetCookieConsent = resetCookieConsent
}
</script>

<style scoped>
.cookie-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-top: 3px solid #0056a3;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  transform: translateY(100%);
  transition: transform 0.3s ease;
  padding: 1rem 0;
}

.cookie-banner.show {
  transform: translateY(0);
}

.cookie-banner-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  gap: 2rem;
}

.cookie-info {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
}

.cookie-icon {
  color: #0056a3;
  font-size: 2rem;
  flex-shrink: 0;
  margin-top: 0.25rem;
}

.cookie-text h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
}

.cookie-text p {
  margin: 0 0 0.75rem 0;
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
}

.cookie-links {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.privacy-link,
.terms-link {
  color: #0056a3;
  text-decoration: none;
  font-weight: 500;
}

.privacy-link:hover,
.terms-link:hover {
  text-decoration: underline;
}

.separator {
  color: #dee2e6;
}

.cookie-actions {
  flex-shrink: 0;
}

.cookie-buttons {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.btn {
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.85rem;
  padding: 0.5rem 1rem;
  border: 1px solid;
  transition: all 0.2s ease;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

.btn-primary {
  background: linear-gradient(135deg, #0056a3 0%, #003d75 100%);
  border-color: transparent;
  color: white;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
}

.btn-outline-secondary {
  background: transparent;
  border-color: #6c757d;
  color: #6c757d;
}

.btn-outline-secondary:hover {
  background: #6c757d;
  color: white;
}

.btn-outline-primary {
  background: transparent;
  border-color: #0056a3;
  color: #0056a3;
}

.btn-outline-primary:hover {
  background: #0056a3;
  color: white;
}

/* Cookie Settings Modal */
.cookie-settings-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.modal-content {
  position: relative;
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: #2c3e50;
}

.btn-close {
  background: none;
  border: none;
  color: #6c757d;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background: #f8f9fa;
  color: #2c3e50;
}

.modal-body {
  padding: 2rem;
}

.cookie-category {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #f1f3f4;
}

.cookie-category:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.category-header {
  margin-bottom: 0.75rem;
}

.category-toggle {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  font-weight: 500;
  color: #2c3e50;
}

.category-toggle input[type="checkbox"] {
  appearance: none;
  width: 20px;
  height: 20px;
  border: 2px solid #dee2e6;
  border-radius: 4px;
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease;
}

.category-toggle input[type="checkbox"]:checked {
  background: #0056a3;
  border-color: #0056a3;
}

.category-toggle input[type="checkbox"]:checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 12px;
  font-weight: bold;
}

.category-toggle input[type="checkbox"]:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.category-title {
  flex: 1;
  font-size: 1rem;
}

.required-badge {
  background: #28a745;
  color: white;
  font-size: 0.7rem;
  padding: 0.2rem 0.5rem;
  border-radius: 12px;
  font-weight: 600;
}

.category-description {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0;
  padding-left: 2.75rem;
}

.modal-footer {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding: 1.5rem 2rem;
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
  border-radius: 0 0 12px 12px;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .cookie-banner-content {
    flex-direction: column;
    gap: 1rem;
  }
  
  .cookie-info {
    text-align: center;
  }
  
  .cookie-buttons {
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    width: 100%;
  }

  .cookie-buttons .btn {
    font-size: 0.8rem;
    padding: 0.45rem 0.75rem;
    flex: 1 1 auto;
    min-width: 5.5rem;
    justify-content: center;
  }
  
  .modal-content {
    margin: 1rem;
    max-height: 90vh;
  }
  
  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 1rem;
  }
  
  .category-description {
    padding-left: 0;
    margin-top: 0.5rem;
  }
  
  .modal-footer {
    flex-direction: column;
  }
}
</style>
